import Helper from "@/helpers/helpers.js" 
class VideoProcessor {
  constructor(videoUrl, baseURL = '') {
    this.videoUrl = videoUrl;
    this.baseURL = baseURL;
    this.videoElement = document.createElement('video');
    this.videoElement.muted = true;

    // Configuración CORS - sin CORS para evitar problemas
    this.videoElement.crossOrigin = null;
    this.videoElement.playsInline=true;
    
    // Smart preload strategy - will be set after size detection
    this.videoElement.preload="metadata";

    // Asignar la URL del video
    this.videoElement.src = `${this.videoUrl}`;
    this.loadingPercentage = 0;
    this.frame = null;
    this.loading = false;
    this.videoSize = 0;

    // Eventos
    this.videoElement.addEventListener('progress', this.onProgress.bind(this));
    this.videoElement.addEventListener('canplay', this.onCanPlay.bind(this));
    this.videoElement.addEventListener('error', this.onError.bind(this));
    this.videoElement.addEventListener('loadedmetadata', this.onMetadataLoaded.bind(this));
    
    // Initialize smart preload after metadata is loaded
    this.videoElement.addEventListener('loadedmetadata', this.initializeSmartPreload.bind(this));
    
    // Handle CORS errors and retry with different strategies
    this.videoElement.addEventListener('error', this.handleCorsError.bind(this));
  }

  onError(e) {

  }

  onMetadataLoaded() {
    
  }

  onProgress() {
    if (this.videoElement.buffered.length > 0) {
      const bufferedEnd = this.videoElement.buffered.end(this.videoElement.buffered.length - 1);
      const duration = this.videoElement.duration;
      this.loadingPercentage = Math.floor((bufferedEnd / duration) * 100);
     
    }
  }

  onCanPlay() {
    this.loading = false;
    
  }


  getFrame() {
    return this.frame;
  }

  getLoadingPercentage() {
    return this.loadingPercentage;
  }

  isLoading() {
    return this.loading;
  }

  load() {
    this.videoElement.load(); // Carga el video
  }

  async initializeSmartPreload() {
    try {
      // Try to get video size from server with multiple fallback methods
      console.log('Initializing smart preload for video:', this.videoUrl);
      let videoSizeMB = 0;
      
      // Method 1: Try HEAD request
      try {
        const response = await fetch(this.videoUrl, { 
          method: 'HEAD',
          mode: 'cors',
          credentials: 'omit'
        });
        
        if (response.ok) {
          const contentLength = response.headers.get('content-length');
          this.videoSize = contentLength ? parseInt(contentLength) : 0;
          videoSizeMB = this.videoSize / (1024 * 1024);
          console.log(`Video size detected via HEAD: ${videoSizeMB.toFixed(1)}MB`);
        }
      } catch (headError) {
        
        
        // Method 2: Try with axios (which might handle CORS better)
        try {
          const axios = (await import('axios')).default;
          const response = await axios.head(this.videoUrl, {
            timeout: 5000,
            headers: {
              'Accept': '*/*',
              'Range': 'bytes=0-1' // Request minimal range to get headers
            }
          });
          
          const contentLength = response.headers['content-length'];
          this.videoSize = contentLength ? parseInt(contentLength) : 0;
          videoSizeMB = this.videoSize / (1024 * 1024);
          console.log(`Video size detected via axios: ${videoSizeMB.toFixed(1)}MB`);
          
        } catch (axiosError) {
          console.warn('Axios HEAD request also failed:', axiosError.message);
          
          // Method 3: Estimate based on URL or use default strategy
          videoSizeMB = this.estimateVideoSize();
        }
      }
      
      // Smart preload strategy based on video size
      if (videoSizeMB > 100) {
        // Very large videos (>100MB): metadata only
        this.videoElement.preload = 'metadata';
        console.log(`Large video detected (${videoSizeMB.toFixed(1)}MB): Using metadata preload`);
      } else if (videoSizeMB > 50) {
        // Large videos (50-100MB): none initially, load on demand
        this.videoElement.preload = 'none';
        console.log(`Medium video detected (${videoSizeMB.toFixed(1)}MB): Using on-demand loading`);
      } else {
        // Small videos (<50MB): auto preload
        this.videoElement.preload = 'auto';
        console.log(`Small video detected (${videoSizeMB.toFixed(1)}MB): Using auto preload`);
      }
      
    } catch (error) {
      console.warn('Could not determine video size, using metadata preload:', error);
      this.videoElement.preload = 'metadata';
    }
  }

  estimateVideoSize() {
    // Try to estimate video size based on URL patterns or use conservative default
    const url = this.videoUrl.toLowerCase();
    
    // If URL contains size indicators, use them
    if (url.includes('large') || url.includes('hd') || url.includes('1080')) {
      return 150; // Assume large video
    } else if (url.includes('small') || url.includes('thumb')) {
      return 10; // Assume small video
    }
    
    // Default to medium size for unknown videos
    
    return 75; // Default to medium size
  }

  handleCorsError(event) {
    const error = event.target.error;
    if (error && error.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
      
      
      // Try without CORS
      this.videoElement.crossOrigin = null;
      this.videoElement.load();
    }
  }

  /*
     Timeout configurable (30s o lo que necesites)
     Cancelación automática si llamas a otra captura
     Cancelación manual (cancelCapture())
     Liberación completa de eventos y timeout
     Código robusto y seguro en navegadores modernos
  */
  /**
   * Captura frame usando el endpoint del servidor
   * @param {Object} data - Datos del frame (maxProbData)
   * @param {number} timeoutMs - Timeout en milisegundos
   * @returns {Promise<Object>} - Objeto con framePhoto y croppedFacePhoto
   */
  async captureFrame(data, timeoutMs = 10000) {
   
    
    try {
      // Preparar los datos para enviar al servidor
      const requestData = {
        frame_idx: data.frame_idx,
        bbox_prob: data.bbox_prob,
        bbox: data.bbox,
        path: data.path,
        faceId: data.faceId,
        similarity: data.similarity,
        id: data.id,
        uuid_user: data.uuid_user,
        photo_uuid: data.photo_uuid,
        infovideo: data.infovideo,
        currentTime: data.currentTime,
        isVideoLoaded: data.isVideoLoaded,
        timestamp: data.timestamp
      };

      

      // Hacer la petición al endpoint
      const response = await fetch(this.baseURL + '/api/v1/face/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestData)
      });


      const result = await response.json();
      



      // Convertir base64 a blob URLs para usar en la UI
      const framePhotoUrl = this.base64ToBlobUrl(result.data.framePhoto);
      const croppedFacePhotoUrl = this.base64ToBlobUrl(result.data.croppedFacePhoto);

      const finalResult = {
        framePhoto: framePhotoUrl,
        croppedFacePhoto: croppedFacePhotoUrl,
        timestamp: result.data.timestamp,
        bbox: result.data.bbox,
        originalData: result.data
      };

      this.frame = croppedFacePhotoUrl; // Para compatibilidad con código existente
      return finalResult;

    } catch (error) {
      
    }
  }

  /**
   * Convierte base64 a blob URL
   * @param {string} base64String - String base64 de la imagen
   * @returns {string} - Blob URL
   */
  base64ToBlobUrl(base64String) {
    try {
      // Remover el prefijo data:image/...;base64, si existe
      const base64Data = base64String.replace(/^data:image\/[a-z]+;base64,/, '');
      
      // Convertir base64 a bytes
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      
      // Crear blob
      const blob = new Blob([byteArray], { type: 'image/png' });
      
      // Crear URL del blob
      return URL.createObjectURL(blob);
    } catch (error) {
      
    }
  }

// Método público para cancelar manualmente
cancelCapture() {
  if (this.captureReject) {
    this.captureReject();
    this.clearCaptureHandlers();
  }
}

}

export default VideoProcessor;
